import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import "./Home.css";
import start_instructions from "../Assets/Home_Page/audio.png";
import scan_instructions from "../Assets/Home_Page/scan_audio.png";
import start_audio from "../Assets/Home_Page/start_audio.mp3";
import scan_audio from "../Assets/Home_Page/Scan_to_Start_audio.mp3"
import start_button from "../Assets/Home_Page/Start.png";
import scan_to_start from "../Assets/Home_Page/Scan_to_Start.png";
import try_the_game from "../Assets/Home_Page/Try_the_Game.png"
import testData from '../Viscat-test-data.json';
import {
  pickRandomPattern,
  resolvePatternFromGetResponse,
  savePatternPath,
} from '../viscatPatterns.js';
import PatternPicker from '../components/PatternPicker';
import { testToolsEnabled } from '../testTools';

const { backEndBaseURL } = require('../config');

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasTestUID = Boolean(new URLSearchParams(location.search || '').get('testUID'));
  const audioRef = useRef(new Audio(hasTestUID ? start_audio : scan_audio)); // Reference to the Audio element

  const [isTapHereVisible, setIsTapHereVisible] = useState(() => {
    return sessionStorage.getItem('homeWelcomeDismissed') !== 'true';
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showURLInputLink, setShowURLInputLink] = useState(false);
  // VISCAT-386 - non-production pattern picker
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    audio.load(); // Load the audio
    audio.play()
      .catch(error => console.error("Failed to play the audio:", error));

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const testUID = params.get('testUID');
    const skipWelcome = params.get('skipWelcome') === 'true';
    const isDemoMode = localStorage.getItem("isDemoMode") === "true";

    if (testUID) {
      setIsTapHereVisible(false);
      setShowURLInputLink(false);
    } else {
      if (!isTapHereVisible || isDemoMode || skipWelcome) {
        setIsTapHereVisible(false);
        setShowURLInputLink(false);
        if (isDemoMode) {
          localStorage.removeItem("isDemoMode");
        }
      }
    }
  }, [location.search, isTapHereVisible]);

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play().catch(error => console.error("Failed to play the audio:", error));
    } else {
      audio.pause();
    }
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setErrorMessage('');
    localStorage.setItem('testUID', '');
    navigate('/');
  };

  const requestFullScreen = () => {
    hideButton();
    localStorage.removeItem('returnedFromTest');
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    htmlElement.classList.add('pseudo-fullscreen');
    bodyElement.classList.add('pseudo-fullscreen');
    localStorage.setItem('pseudoFullscreen', 'true');
    const event = new CustomEvent('pseudoFullscreenChange', {
      detail: { isFullScreen: true }
    });
    window.dispatchEvent(event);
  }

  function hideButton() {
    setIsTapHereVisible(false);
    sessionStorage.setItem('homeWelcomeDismissed', 'true');

    // After clicking Welcome, always show Start button (regardless of testUID)
    setShowURLInputLink(false);

    resetLocalStorage();
    toggleAudio();
  }

  function resetLocalStorage() {
    localStorage.clear();
  }

  async function checkTestUID(testUID) {
    try {
      const result = await fetch(`${backEndBaseURL}/v2/candidateTest/${testUID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      if (!result.ok) { // Check if response status is invalid 
        console.error(`Failed to check test UID ${testUID}: ${result.status}`);
        showErrorModal('There was an issue submitting your results. Please ask for assistance.  '
          + (result ? ` (Error code: ${result.status})` : '')
          + (result ? ` URL: ${result.url}` : 'N/A'));
        throw new Error(`HTTP error! status: ${result.status}`);
      }
      else {
        return new Promise(function (resolve, reject) {
          resolve(result);
        });
      }
    } catch (e) {
      return null;
    }
  }

  /*
    Starts a demo run with the supplied catalog entry. The pattern arrives as an
    argument rather than through localStorage so a caller that has already chosen
    one cannot have it overwritten by the random pick below.

    wasPicked records HOW the pattern was chosen. The score summary on the Thank You
    page is shown only for a deliberately picked pattern, not for a random "Try the
    Game" run, so the two cases have to stay distinguishable downstream. It is
    written on both branches rather than only when true, so a later random run
    cannot inherit a stale marker from an earlier picked one.
  */
  function startDemo(pattern, wasPicked) {
    localStorage.removeItem("testUID");
    localStorage.setItem("testLevel", 0);
    localStorage.setItem("isDemoMode", "true");
    if (wasPicked) {
      localStorage.setItem("isPickedPattern", "true");
    } else {
      localStorage.removeItem("isPickedPattern");
    }
    savePatternPath(pattern);
    navigate('/VisCAT_test');
  }

  function handleDemo() {
    startDemo(pickRandomPattern(testData.patterns), false);
  }

  // VISCAT-386 (non-production only): identical to handleDemo except the pattern
  // is chosen from the catalog rather than picked at random.
  function handlePatternChosen(pattern) {
    setIsPickerOpen(false);
    startDemo(pattern, true);
  }

  async function goToTestingPage() {
    const params = new URLSearchParams(location.search || '');
    const value = params.get('testUID');

    if (value) {
      localStorage.setItem("testUID", value);
      var response = await checkTestUID(value);

      if (!response) {
        localStorage.setItem("testLevel", 0);
        return;
      }

      if (response.status === 200) {
        var tempResponse = await response.json();
        var { JWT, nextLevel } = tempResponse;
        // we are doing a fudge here because we know there is a practice level
        // TODO change the following and handle all level logic in BE so that we deal by level names
        // solution must work in stand alone mode.
        if (nextLevel[0] !== 'Direct Copy') {
          // we alaways start at the practice page
          // so no change to next level
          // if we have done the DC level, then we need skip the practic page.
          nextLevel[1]++;
        }
        localStorage.setItem("testLevel", Number(nextLevel[1]));
        localStorage.setItem("JWT", JWT);
        try {
          savePatternPath(resolvePatternFromGetResponse(tempResponse, testData.patterns));
        } catch (e) {
          showErrorModal('Please ask for help.\n\nThe test pattern from the server was invalid.');
          return;
        }
      }
      else if (response.status === 220) {
        navigate('/TestCompleted');
        return;
      }
      else if (response.status === 204) {
        const msg = `Please ask for help.\n\nThe QR Code for testUID "${value}" is not valid.`;
        showErrorModal(msg);
        return;
      }
      else {
        localStorage.setItem("testLevel", 0);
      }
    }
    else {
      return;
    }
    navigate('/VisCAT_test');
  }


  return (
    <div className='home_background'>
      <div className="home_instructions_container">
        <img src={hasTestUID ? start_instructions : scan_instructions} alt="Home Instructions" className='home_instructions' onClick={toggleAudio} />
      </div>
      {isTapHereVisible && (
        <div className='InteractionButton' onClick={requestFullScreen}>
          Welcome
        </div>
      )}

      {!isTapHereVisible && !showURLInputLink && (() => {
        if (hasTestUID) {
          return (
            <div className='Home_buttons'>
              <div>
                <img className='Start_Home' onClick={goToTestingPage} src={start_button} alt="Start Button" />
              </div>
            </div>
          );
        }
        return (
          <div className='Home_two_buttons'>
            <div>
              <img className='Start_Home' onClick={() => navigate('/url-input')} src={scan_to_start} alt="Scan to Start Button" />
            </div>
            <div>
              <img className='Start_Home' onClick={handleDemo} src={try_the_game} alt="Try the Game" />
            </div>
            {testToolsEnabled() && (
              <div>
                <button
                  type="button"
                  className='Home_pattern_picker_button'
                  onClick={() => setIsPickerOpen(true)}
                >
                  Pick a pattern
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/*
        VISCAT-386 - the sheet is a sibling of .Home_two_buttons, never a child:
        that element's transform makes it the containing block for position:fixed
        descendants and its z-index caps their paint order.
      */}
      {testToolsEnabled() && isPickerOpen && (
        <PatternPicker
          patterns={testData.patterns}
          gridSize={testData['grid-size']}
          onSelect={handlePatternChosen}
          onCancel={() => setIsPickerOpen(false)}
        />
      )}

      {!isTapHereVisible && showURLInputLink && (
        <div className='url-input-link-container'>
          <button
            className='url-input-link-button'
            onClick={() => navigate('/url-input')}
          >
            Scan QR Code
          </button>
        </div>
      )}

      <div className='About_Us'>
        <a href="https://viscat.com.au/about-us-1" target="_blank" rel="noopener noreferrer" style={{ color: "black" }}>
          <p>About us</p>
        </a>
      </div>

      {isModalVisible && (
        <div className='modal'>
          <div className='modal_content'>
            <span className='close' onClick={closeModal}>&times;</span>
            <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
